/** How the page names, colours and dates what the panel sends. */

import type { DayStatus, Impact, Kind, State, Status, StatusEvent } from './api'

export const STATUS: Record<
  DayStatus,
  { label: string; text: string; bg: string; ring: string; hex: string }
> = {
  operational: {
    label: 'Operativo',
    text: 'text-ok',
    bg: 'bg-ok',
    ring: 'ring-ok/30',
    hex: '#3dd68c',
  },
  maintenance: {
    label: 'In manutenzione',
    text: 'text-maint',
    bg: 'bg-maint',
    ring: 'ring-maint/30',
    hex: '#5b8def',
  },
  degraded: {
    label: 'Rallentato',
    text: 'text-slow',
    bg: 'bg-slow',
    ring: 'ring-slow/30',
    hex: '#f5c542',
  },
  partial: {
    label: 'Disservizio parziale',
    text: 'text-part',
    bg: 'bg-part',
    ring: 'ring-part/30',
    hex: '#ff8a3d',
  },
  major: {
    label: 'Disservizio grave',
    text: 'text-down',
    bg: 'bg-down',
    ring: 'ring-down/30',
    hex: '#ef4444',
  },
  none: {
    label: 'Nessun dato',
    text: 'text-mist',
    bg: 'bg-white/10',
    ring: 'ring-white/10',
    hex: '#93a0bc',
  },
}

/** The big line at the top. */
export const OVERALL: Record<Status, string> = {
  operational: 'Tutti i servizi sono operativi',
  maintenance: 'Manutenzione in corso',
  degraded: 'Alcuni servizi sono rallentati',
  partial: 'Disservizio parziale in corso',
  major: 'Disservizio grave in corso',
}

export const KIND: Record<Kind, string> = {
  incident: 'Incidente',
  maintenance: 'Manutenzione',
  notice: 'Annuncio',
}

export const STATE: Record<State, string> = {
  investigating: 'In verifica',
  identified: 'Causa individuata',
  monitoring: 'Sotto osservazione',
  resolved: 'Risolto',
  scheduled: 'Programmata',
  in_progress: 'In corso',
  verifying: 'In verifica',
  completed: 'Completata',
  active: 'Attivo',
  ended: 'Concluso',
}

export const IMPACT: Record<Impact, string> = {
  none: 'Nessun impatto',
  minor: 'Impatto lieve',
  major: 'Impatto grave',
  critical: 'Impatto critico',
  maintenance: 'Manutenzione',
}

export const NOTICE_LEVEL: Record<Impact, string> = {
  none: 'Informazione',
  minor: 'Attenzione',
  major: 'Importante',
  critical: 'Importante',
  maintenance: 'Informazione',
}

/** The colour an event wears: green once closed, blue for maintenance, else by impact. */
export function tone(event: Pick<StatusEvent, 'kind' | 'impact' | 'state'>): DayStatus {
  if (['resolved', 'completed', 'ended'].includes(event.state)) return 'operational'
  if (event.kind === 'maintenance') return 'maintenance'
  if (event.kind === 'notice') {
    return event.impact === 'major'
      ? 'partial'
      : event.impact === 'minor'
        ? 'degraded'
        : 'maintenance'
  }
  const byImpact: Record<Impact, DayStatus> = {
    none: 'none',
    minor: 'degraded',
    major: 'partial',
    critical: 'major',
    maintenance: 'maintenance',
  }
  return byImpact[event.impact]
}

/** Every date on the page is Italian time: the days of the bars are Italian days. */
const TZ = 'Europe/Rome'

const DATE_TIME = new Intl.DateTimeFormat('it-IT', {
  timeZone: TZ,
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})
const DATE_TIME_YEAR = new Intl.DateTimeFormat('it-IT', {
  timeZone: TZ,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})
const TIME = new Intl.DateTimeFormat('it-IT', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })
const WEEKDAY_DATE = new Intl.DateTimeFormat('it-IT', {
  timeZone: TZ,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})
const LONG_DAY = new Intl.DateTimeFormat('it-IT', {
  timeZone: TZ,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})
const MONTH = new Intl.DateTimeFormat('it-IT', { timeZone: TZ, month: 'long', year: 'numeric' })
const DAY_KEY = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export function when(value: string): string {
  const date = new Date(value)
  const sameYear = DAY_KEY.format(date).slice(0, 4) === DAY_KEY.format(new Date()).slice(0, 4)
  return (sameYear ? DATE_TIME : DATE_TIME_YEAR).format(date)
}

export const clock = (value: string | number) => TIME.format(new Date(value))
export const weekday = (value: string) => WEEKDAY_DATE.format(new Date(value))
export const monthOf = (value: string) => capital(MONTH.format(new Date(value)))

/** 2026-09-30 for an instant, in Italian time: the key the bars' days use. */
export const dayKey = (value: string | Date) => DAY_KEY.format(new Date(value))

/** A bar's day ("2026-09-30") as words: "mercoledì 30 settembre". */
export function longDay(day: string): string {
  // Noon, so no time zone can move it to another day.
  return LONG_DAY.format(new Date(`${day}T12:00:00Z`))
}

export function capital(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/** "1 h 20 min", "3 giorni". */
export function span(from: string, to: string | null | undefined): string {
  const minutes = Math.max(
    0,
    Math.round((new Date(to ?? Date.now()).getTime() - new Date(from).getTime()) / 60_000),
  )
  return minutesLabel(minutes)
}

export function minutesLabel(minutes: number): string {
  if (minutes < 1) return 'meno di un minuto'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours < 24) return rest ? `${hours} h ${rest} min` : `${hours} h`
  const days = Math.floor(hours / 24)
  const hoursLeft = hours % 24
  const daysLabel = days === 1 ? '1 giorno' : `${days} giorni`
  return hoursLeft ? `${daysLabel} ${hoursLeft} h` : daysLabel
}

/** "tra 2 h", "tra 3 giorni", "a breve". */
export function until(value: string): string {
  const minutes = Math.round((new Date(value).getTime() - Date.now()) / 60_000)
  if (minutes <= 1) return 'a breve'
  return `tra ${minutesLabel(minutes)}`
}

export function percent(value: number | null): string {
  if (value === null) return '—'
  const digits = value >= 99.995 ? 0 : value >= 99 ? 2 : 1
  return `${value.toLocaleString('it-IT', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`
}
